import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";
import { useCallback, useEffect, useMemo, useState } from "react";

/** Specialty from GET /processed-files/result/:result_id response */
export type ProcessedFileSpecialty = "endoscopy" | "haematology" | "chemo" | "well_baby" | "ophthalmology";

/** Single file in GET /processed-files/result/:result_id response */
export type ProcessedFileResultItem = {
  id: string;
  objectKey: string;
  bucket: string;
  inboundClassificationResultId: string;
  type: string;
  text: string;
  title: string;
  createdAt: string;
  specialty: ProcessedFileSpecialty;
  providerId: string;
};

/** Response body for GET /processed-files/result/:result_id */
export type ProcessedFileResultResponse = {
  files: ProcessedFileResultItem[];
};

/** Legacy shape used by inbound data files factory (different endpoint). */
export interface ProcessedFile {
  id: string;
  customerId: string;
  patientId: string;
  observationIdentifier: "endoscopy" | "histology";
  observationDatetime: Date;
  observationResult: string;
  observationResultType: "plaintext" | "pdf";
  observationId: string;
  patientAge: number;
  providerId: string;
  encounterId?: string;
}

/** Document type of a processed file. Distinct vocabulary from the OBX `observationIdentifier`; never assign across the two. */
export type ProcessedFileIdentifier =
  | "endoscopy_procedure"
  | "endoscopy_histology"
  | "comorbidity_doc"
  | "discharge"
  | "discharge_cancelled"
  | "discharge_letter"
  | "problem_list"
  | "pre_op_endo"
  | "referral_letter"
  | "sact"
  | "cancer_mdt"
  | "codeable_summary";

export interface ProcessedFileText {
  id: string;
  text: string;
  title: string;
  fileIdentifier: ProcessedFileIdentifier;
  /** The S3 key the file was read from; a verdict mention carries the same key. */
  objectKey?: string;
  observationId?: string;
  textType?: "base64" | "plain";
  observationDatetime?: Date;
  patientAge?: number;
  providerId?: string;
  encounterId?: string;
  specialty?: ProcessedFileSpecialty;
}

interface UseProcessedFilesResult {
  data: ProcessedFileText[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useProcessedFiles(resultId?: string): UseProcessedFilesResult {
  const [files, setFiles] = useState<ProcessedFileText[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFiles = useCallback(async () => {
    if (!resultId) {
      setFiles([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetchWithSupabaseAuth(`${QA_API_DOMAIN}/processed-files/result/${resultId}`);

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        const message = errorData?.error ?? errorData?.message ?? res.statusText;
        setError(message);
        setFiles([]);
        return;
      }

      const data = (await res.json()) as ProcessedFileResultResponse;
      const allFiles: ProcessedFileText[] = data.files.map(
        (file): ProcessedFileText => ({
          id: file.id,
          text: file.text,
          title: file.title,
          fileIdentifier: file.type as ProcessedFileIdentifier,
          objectKey: file.objectKey,
          textType: "plain",
          specialty: file.specialty,
          providerId: file.providerId,
        }),
      );
      setFiles(allFiles);
    } catch (err: unknown) {
      let message = "Unknown error";
      if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, [resultId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const stableValue = useMemo(() => {
    return {
      data: files,
      isLoading,
      error,
      refetch: fetchFiles,
    };
  }, [error, fetchFiles, isLoading, files]);

  return stableValue;
}
